import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpBackend, HttpParams } from '@angular/common/http';
import { concat, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, tap, map, filter } from 'rxjs/operators';
import { departementCODE, demographicCriteria, googleKey, ciCriteria } from '../../models/smsadv/config';
import { UntypedFormBuilder, UntypedFormGroup, Validators, UntypedFormArray } from '@angular/forms';
import { Options, LabelType } from '@angular-slider/ngx-slider';
import { SmsAdvService } from '@services/smsadv.service';
import * as _ from 'lodash';
import { AtlasToastService } from '@services/atlas-toast.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'wsb-smsadv',
  templateUrl: './smsadv.component.html',
  styleUrls: ['./smsadv.component.scss'],
  standalone: false
})
export class SmsadvComponent implements OnInit {
  zips$: Observable<any>;
  zipsLoading = false;
  zipsInput$ = new Subject<string>();
  selectedZip: any;
  minLengthTerm = 2;
  httpClient: HttpClient;
  urlGeocode = 'https://maps.google.com/maps/api/geocode/json';
  searchForm: UntypedFormGroup;
  demographicCriteriaItems = demographicCriteria;
  ciCriteriaItems: any = ciCriteria;
  zipsInfo = { CP: {}, Dept: {} };
  totalVolumeCountAvalaible = 0;
  totalVolumeCountDesired = 0;
  dataVolume: any = [];
  minValueSliderRadius = 5;

  optionsSliderDemoGraph: Options = {
    floor: 18,
    ceil: 65,
    step: 1,
    showTicks: true,
    translate: (value: number, label: LabelType): string => {
      if (value === this.optionsSliderDemoGraph.ceil) {
        return value + ' ans +';
      }
      return value + ' ans';
    }
  };
  minValueSliderDemograph = 18;
  maxValueSliderDemograph = 65;
  modalAskEstimateModal: NgbModalRef;

  constructor(
    handler: HttpBackend,
    private formBuilder: UntypedFormBuilder,
    private smsAdvService: SmsAdvService,
    private modalService: NgbModal,
    private atlasToast: AtlasToastService
  ) {
    this.httpClient = new HttpClient(handler);
    this.searchForm = this.formBuilder.group({
      zips: ['', [Validators.required]],
      sexe: ['mixte', [Validators.required]],
      age: ['18', [Validators.required]],
      ci: [''],
      estimate: this.formBuilder.array([])
    });
  }

  ngOnInit() {
    this.loadZips();
    this.estimate.valueChanges.subscribe(data => {
      this.resetVolumeCount(data);
    });
  }

  trackByFn(item: any) {
    return item.id;
  }

  resetVolumeCount(data) {
    this.totalVolumeCountDesired = 0;
    this.totalVolumeCountAvalaible = 0;
    data.forEach(subdata => {
      this.totalVolumeCountDesired += subdata.quantityDesired;
      this.totalVolumeCountAvalaible += subdata.quantityAvailable;
    });
  }

  loadZips() {
    this.zips$ = concat(
      of([]), // default items
      this.zipsInput$.pipe(
        filter(res => {
          return res !== null && res.length >= this.minLengthTerm;
        }),
        distinctUntilChanged(),
        debounceTime(800),
        tap(() => (this.zipsLoading = true)),
        switchMap(term => {
          return this.getZips(term).pipe(
            catchError(() => of([])) // empty list on error
            // tap(() => this.zipsLoading = false)
          );
        }),
        switchMap(data => {
          if (data.status === 'OK') {
            return this.formatZipAddress(data.results[0]).pipe(
              catchError(() => of([])), // empty list on error
              tap(() => (this.zipsLoading = false))
            );
          }
          return of([]);
        })
      )
    );
  }

  search() {
    const formValue = this.searchForm.getRawValue();
    const jsonQuery: any = {};
    jsonQuery.query = 'volume';
    jsonQuery.agemin = formValue.age[0] === 18 ? '' : formValue.age[0];
    jsonQuery.agemax = formValue.age[1] === 65 ? '' : formValue.age[1];
    jsonQuery.civilite = formValue.sexe;
    jsonQuery.dept = '';
    jsonQuery.code_postal = '';
    jsonQuery.ci = formValue.ci.length ? formValue.ci.join() : '';
    formValue.zips.forEach(zip => {
      switch (zip.typeplace) {
        case 'postal_code':
          jsonQuery.code_postal += (jsonQuery.code_postal.length ? ',' : '') + zip.zip;
          this.zipsInfo.CP[zip.zip] = zip.text;
          break;
        case 'administrative_area_level_2,political':
          jsonQuery.dept += (jsonQuery.dept.length ? ',' : '') + zip.zip;
          this.zipsInfo.Dept[zip.zip] = zip.text;
          break;
      }
    });
    this.estimate.clear();
    this.smsAdvService.getCountByZipCode(jsonQuery).subscribe(data => {
      this.dataVolume = data;
      this.dataVolume.forEach(volume => {
        if (volume.Type !== 'total') {
          this.estimate.push(
            this.formBuilder.group({
              zip: [this.zipsInfo[volume.Type][volume.Localite] || 'Total'],
              quantityAvailable: [volume.Volume],
              quantityDesired: [volume.Volume, [Validators.required]]
            })
          );
        }
      });
      this.resetVolumeCount(this.estimate.getRawValue());
    });
  }

  get estimate(): UntypedFormArray {
    return this.searchForm.get('estimate') as UntypedFormArray;
  }

  get estimateControlsArray(): UntypedFormGroup[] {
    return this.estimate.controls as UntypedFormGroup[];
  }

  askestimate() {
    const values = this.searchForm.getRawValue();
    values.agemin = values.age[0] === 18 ? '' : values.age[0];
    values.agemax = values.age[1] === 65 ? '' : values.age[1];
    values.ciLabels = [];
    if (values.ci.length) {
      values.ci.forEach(id => {
        values.ciLabels.push(ciCriteria[_.findIndex(ciCriteria, ['value', id])]);
      });
    }
    delete values.ci;
    delete values.zips;
    delete values.age;
    this.smsAdvService.askEstimate(values).subscribe(
      () => {
        this.atlasToast.success('common_success', null);
      },
      () => {
        this.atlasToast.error('common_error', null);
      },
      () => {
        this.modalAskEstimateModal.close();
      }
    );
    // console.log("values",values)
  }

  openAskEstimate(dialog) {
    this.modalAskEstimateModal = this.modalService.open(dialog);
  }

  getParamsGeocode(term) {
    let params = new HttpParams();
    params = params.append('key', googleKey);
    params = params.append('address', term);
    params = params.append('components', 'country:FR');
    return params;
  }

  getZips(term: string = null): Observable<any> {
    const params = this.getParamsGeocode(term);
    return this.httpClient
      .get<any>(this.urlGeocode, { params })
      .pipe(
        map(resp => {
          if (resp.Error) {
            throwError(resp.Error);
          } else {
            return resp;
          }
        })
      );
  }

  formatZipAddress(objet): Observable<any> {
    let valpostal = '';
    let valcity = '';
    const placeid = objet.place_id;
    const typeplace = objet.types.toString();
    switch (typeplace) {
      case 'postal_code':
      case 'street_address':
        objet.address_components.forEach(address => {
          if (address.types[0] === 'postal_code') {
            valpostal = address.long_name;
          }
          if (address.types[0] === 'locality') {
            valcity = address.long_name;
          }
        });
        break;
      case 'administrative_area_level_2,political':
        objet.address_components.forEach(address => {
          if (address.types[0] === 'administrative_area_level_2') {
            valcity = address.long_name;
            valpostal = departementCODE[valcity];
          }
        });
        break;
      case 'country,political':
        objet.address_components.forEach(address => {
          if (address.types[0] === 'country' && address.short_name === 'FR') {
            valcity = address.long_name;
            // getcountry = true;
            valpostal = 'Pays';
          }
        });
        break;
      default:
        break;
    }
    const params = this.getParamsGeocode(valcity);
    if (valpostal.length || valcity.length) {
      return this.httpClient
        .get<any>(this.urlGeocode, { params })
        .pipe(
          map(data => {
            let text = valpostal;
            text += (text.length ? ' - ' : '') + valcity;
            return [
              {
                id: placeid,
                typeplace,
                zip: valpostal,
                text
              }
            ];
          })
        );
    }
    return of({});
  }
}
